import {
  EVENT_QUEUE_JOB_STATES,
  EVENT_QUEUE_METRICS_RANGES,
  INBOUND_EVENT_STATUSES,
  type EventQueueJobState,
  type EventQueueMetricsRange,
  type InboundEventStatus,
} from "@t3tools/contracts";

/**
 * The pages a person moves between. The Inbox answers "does anything need me?";
 * a queue's page (`detail`) is where one queue is debugged; the rest are for
 * digging in and sit lower in the nav.
 */
export const EVENT_QUEUE_VIEWS = ["inbox", "search", "deliveries", "system", "history"] as const;
export type EventQueueView = (typeof EVENT_QUEUE_VIEWS)[number];

/** Tabs from before the Inbox, so links people already shared still land somewhere sensible. */
const LEGACY_TABS: Record<string, EventQueueView> = {
  activity: "history",
  "background-jobs": "system",
  failures: "inbox",
  ingress: "deliveries",
  jobs: "search",
  overview: "inbox",
  storage: "system",
  workers: "system",
};

/** A queue page's job filter: a state, or `all` once someone picks everything over the default. */
export type QueueJobFilter = EventQueueJobState | "all";

/** URL state for `/event-queue`, so a page, a filtered list, or an open job can be shared as a link. */
export type EventQueueSearch = {
  /** History: show only this operator's actions. */
  actor?: string | undefined;
  /** The queue whose page is open. */
  detail?: string | undefined;
  event?: string | undefined;
  /** Job search with qualifiers, such as `subject:github:org/repo#12 error:timeout`. */
  q?: string | undefined;
  queue?: string | undefined;
  rule?: string | undefined;
  source?: string | undefined;
  range?: EventQueueMetricsRange | undefined;
  state?: QueueJobFilter | undefined;
  status?: InboundEventStatus | undefined;
  tab?: EventQueueView | undefined;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_FILTER_LENGTH = 200;
/** The server rejects longer searches. */
export const MAX_JOB_SEARCH_LENGTH = 500;

function readFilter(value: unknown, maxLength = MAX_FILTER_LENGTH): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= maxLength ? trimmed : undefined;
}

export function isInboundEventStatus(value: unknown): value is InboundEventStatus {
  return typeof value === "string" && (INBOUND_EVENT_STATUSES as readonly string[]).includes(value);
}

const isOneOf =
  <T extends string>(values: readonly T[]) =>
  (value: unknown): value is T =>
    typeof value === "string" && (values as readonly string[]).includes(value);

const isView = isOneOf(EVENT_QUEUE_VIEWS);
const isJobFilter = isOneOf<QueueJobFilter>([...EVENT_QUEUE_JOB_STATES, "all"]);
const isMetricsRange = isOneOf(EVENT_QUEUE_METRICS_RANGES);

function readView(value: unknown): EventQueueView | undefined {
  if (isView(value)) return value;
  return typeof value === "string" ? LEGACY_TABS[value] : undefined;
}

/** Unknown or malformed values are dropped rather than sent to the API. */
export function parseEventQueueSearch(search: Record<string, unknown>): EventQueueSearch {
  const parsed: EventQueueSearch = {};
  const tab = readView(search.tab);
  if (tab) parsed.tab = tab;
  const detail = readFilter(search.detail);
  if (detail) parsed.detail = detail;
  if (isJobFilter(search.state)) parsed.state = search.state;
  if (isMetricsRange(search.range)) parsed.range = search.range;
  if (isInboundEventStatus(search.status)) parsed.status = search.status;
  const queue = readFilter(search.queue);
  if (queue) parsed.queue = queue;
  const source = readFilter(search.source);
  if (source) parsed.source = source;
  const rule = readFilter(search.rule);
  if (rule) parsed.rule = rule;
  const q = readFilter(search.q, MAX_JOB_SEARCH_LENGTH);
  if (q) parsed.q = q;
  const actor = readFilter(search.actor);
  if (actor) parsed.actor = actor;
  if (typeof search.event === "string" && UUID_PATTERN.test(search.event)) {
    parsed.event = search.event;
  }
  return parsed;
}

/**
 * The page to show. A link that carries only job filters opens Search, since
 * that's what those filters narrow.
 */
export function resolveEventQueueView(search: EventQueueSearch): EventQueueView {
  if (search.tab) return search.tab;
  return search.status || search.queue || search.rule || search.source || search.q
    ? "search"
    : "inbox";
}

/** Moving to another page adds a history entry so Back returns; narrowing the same page doesn't. */
export function isSamePage(current: EventQueueSearch, next: EventQueueSearch): boolean {
  return (
    current.detail === next.detail && resolveEventQueueView(current) === resolveEventQueueView(next)
  );
}

/** Search filters that live in the URL, as opposed to the query text. */
export const SEARCH_FILTER_KEYS = ["status", "queue", "source", "rule"] as const;
export type SearchFilterKey = (typeof SEARCH_FILTER_KEYS)[number];
